(() => {
  const CONSTANTS = {
    ANNOUNCEMENT_PADDING: 16,
    ANNOUNCEMENT_MIN_ITEM_HEIGHT: 36,
    ANNOUNCEMENT_MIN_ITEM_HEIGHT_DESKTOP: 24, // 34px total height - 10px padding
    ANNOUNCEMENT_DEFAULT_SPEED: 3000,
    RESIZE_DEBOUNCE_MS: 100,
  };

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.announcementIntervals = [];
      this.init();
    }

    init() {
      // Document Level Listeners
      document.addEventListener('click', e => {
        const target = e.target;

        const trigger = target.closest('[data-cart-drawer-toggle], [data-cart-drawer-close]');
        if (trigger) {
          e.preventDefault();
          if (trigger.hasAttribute('data-cart-drawer-toggle')) {
            if (window.location.pathname.includes('/cart')) {
              window.location.reload();
            } else {
              document.body.classList.add('no-scroll');
              this.open();
            }
          } else {
            document.body.classList.remove('no-scroll');
            this.close();
          }
          return;
        }

        const qtyTrigger = target.closest('[data-qty-change]');
        if (qtyTrigger) {
          const input = qtyTrigger
            .closest('[data-qty-selector]')
            ?.querySelector('[data-qty-input]');
          if (input) {
            input.setCustomValidity('');
            // Also dismiss any active toast by finding it in the DOM (optional)
          }
          this.updateQuantity(qtyTrigger, parseInt(qtyTrigger.dataset.qtyChange));
          return;
        }

        const removeTrigger = target.closest('[data-cart-remove]');
        if (removeTrigger) {
          this.removeItem(removeTrigger);
          return;
        }

        const accordionTrigger = target.closest('[data-accordion-trigger]');
        if (accordionTrigger) {
          this.toggleAccordion(accordionTrigger);
          return;
        }

        const footerBackdrop = target.closest('[data-footer-backdrop]');
        if (footerBackdrop) {
          this.closeAllAccordions();
          return;
        }
      });

      document.addEventListener('change', e => {
        if (e.target.hasAttribute('data-qty-input')) {
          e.target.setCustomValidity('');
          this.updateQuantity(e.target, 0);
        }
      });

      document.addEventListener('cart:item-added', () => this.refreshDrawer(true));
      document.addEventListener('flu:cart:item-added', () => this.refreshDrawer(true));

      document.addEventListener('submit', e => {
        if (e.target.action?.includes('/cart/add')) {
          e.preventDefault();
          this.addFormToCart(e.target);
        }
      });

      this.initUIComponents();
    }

    async addFormToCart(form) {
      const button = form.querySelector('[data-add-to-cart]');
      if (button) {
        button.classList.add('bttn--loading');
        button.setCustomValidity?.('');
      }

      try {
        const formData = new FormData(form);
        const rootUrl = window.Shopify?.routes?.root || '/';
        const res = await fetch(`${rootUrl}cart/add.js`.replace(/\/+/g, '/'), {
          method: 'POST',
          body: formData,
        });

        if (res.ok) {
          await this.refreshDrawer(true);
        } else {
          const error = await res.json();
          const message = error.description || error.message || 'Could not add to cart';
          if (button && button.setCustomValidity) {
            button.setCustomValidity(message);
            button.reportValidity();
          } else {
            console.log(message);
          }
        }
      } catch (e) {
        console.error('Add to cart error:', e);
      } finally {
        if (button) button.classList.remove('bttn--loading');
      }
    }

    initUIComponents(savedState = {}) {
      this.initAnnouncementSliders(savedState);

      try {
        if (window.neptune?.donation?.init) {
          return window.neptune.donation.init();
        }
      } catch (e) {
        console.warn('Donation init failed:', e);
      }
      return Promise.resolve();
    }

    async _renderView() {
      return await this.refreshDrawer();
    }

    initAnnouncementSliders(savedState = {}) {
      if (this.announcementIntervals) {
        this.announcementIntervals.forEach(clearInterval);
        this.announcementIntervals = [];
      }

      const sliders = document.querySelectorAll('[data-cart-announcements]');
      sliders.forEach(slider => {
        this.initSingleSlider(slider, savedState);
      });
    }

    initSingleSlider(slider, savedState = {}) {
      const items = slider.querySelectorAll('[data-announcement-item]');
      const isHorizontal =
        window.innerWidth >= 1120 && slider.classList.contains('cart-page__announcements');
      const padding = isHorizontal ? 10 : CONSTANTS.ANNOUNCEMENT_PADDING;
      const minHeight = isHorizontal
        ? CONSTANTS.ANNOUNCEMENT_MIN_ITEM_HEIGHT_DESKTOP
        : CONSTANTS.ANNOUNCEMENT_MIN_ITEM_HEIGHT;

      if (!items.length) {
        slider.style.height = isHorizontal ? '34px' : '';
        return;
      }

      const calculateHeight = () => {
        let maxItemHeight = 0;
        items.forEach(item => {
          item.style.height = 'auto'; // Reset for calculation
          maxItemHeight = Math.max(maxItemHeight, item.offsetHeight);
        });
        maxItemHeight = Math.max(maxItemHeight, minHeight);
        items.forEach(item => (item.style.height = `${maxItemHeight}px`));
        slider.style.height = `${maxItemHeight + padding}px`;
        return maxItemHeight;
      };

      let maxItemHeight = calculateHeight();

      if (maxItemHeight <= minHeight) {
        setTimeout(() => {
          maxItemHeight = calculateHeight();
        }, 150);
      }

      if (items.length <= 1) return;

      let currentIndex = savedState.announcementIndex || window._cartAnnouncementIndex || 0;
      const speed = parseInt(slider.dataset.speed) || CONSTANTS.ANNOUNCEMENT_DEFAULT_SPEED;

      this.updateSliderState(slider, currentIndex, maxItemHeight, true);

      const interval = setInterval(() => {
        if (!slider.isConnected) {
          clearInterval(interval);
          return;
        }
        currentIndex = (currentIndex + 1) % items.length;
        this.updateSliderState(slider, currentIndex, maxItemHeight);
      }, speed);

      this.announcementIntervals.push(interval);

      slider.querySelectorAll('[data-announcement-dot]').forEach((dot, i) => {
        dot.addEventListener('click', e => {
          e.stopPropagation();
          clearInterval(interval);
          currentIndex = i;
          this.updateSliderState(slider, currentIndex, maxItemHeight);
        });
      });
    }

    updateSliderState(slider, index, maxHeight, immediate = false) {
      const track = slider.querySelector('[data-announcements-track]');
      const dots = slider.querySelectorAll('[data-announcement-dot]');
      if (!track) return;

      window._cartAnnouncementIndex = index;
      const isHorizontal =
        window.innerWidth >= 1120 && slider.classList.contains('cart-page__announcements');
      const gap = isHorizontal ? 0 : 16;
      const padding = isHorizontal ? 10 : CONSTANTS.ANNOUNCEMENT_PADDING;

      let offset;
      if (isHorizontal) {
        const itemWidth = slider.querySelector('[data-announcement-item]').offsetWidth;
        offset = index * itemWidth;
      } else {
        offset = index * (maxHeight + gap);
      }

      if (immediate) {
        slider.style.transition = 'none';
        track.style.transition = 'none';
        track.style.transform = isHorizontal
          ? `translateX(-${offset}px)`
          : `translateY(-${offset}px)`;
        track.offsetHeight;
        slider.style.transition = '';
        track.style.transition = '';
      } else {
        track.style.transform = isHorizontal
          ? `translateX(-${offset}px)`
          : `translateY(-${offset}px)`;
      }

      slider.style.height = `${maxHeight + padding}px`;
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }

    toggleAccordion(trigger) {
      const accordion = trigger.closest('[data-accordion]');
      if (!accordion) return;
      const isOpen = accordion.classList.toggle('is-open');

      // Only trigger backdrop if not explicitly disabled for this accordion
      if (accordion.hasAttribute('data-no-backdrop')) return;

      // Determine the correct backdrop to show.
      // If we're inside the cart drawer, only show the one inside the drawer.
      // If we're on the cart page (sticky footer), show the one on the page.
      const container = trigger.closest('[data-cart-drawer]') || document;
      const footerBackdrop = container.querySelector('[data-footer-backdrop]');

      if (footerBackdrop) {
        footerBackdrop.classList.toggle('is-active', isOpen);
      }

      // Toggle a class on the container to allow styling other elements (like the header border)
      container.classList.toggle('footer-backdrop-active', isOpen);
    }

    closeAllAccordions() {
      document.querySelectorAll('[data-accordion].is-open').forEach(acc => {
        acc.classList.remove('is-open');
      });
      document
        .querySelectorAll('[data-footer-backdrop]')
        .forEach(el => el.classList.remove('is-active'));
      document
        .querySelectorAll('.footer-backdrop-active')
        .forEach(el => el.classList.remove('footer-backdrop-active'));
    }

    open() {
      // Only open the DRAWER element, not everything with data-cart-drawer
      const drawerElement = document.getElementById('cart-drawer');
      const recsElement = document.querySelector('cart-recommendations');
      if (drawerElement) {
        drawerElement.classList.add('is-open');

        if (recsElement != null) {
          try {
            var model = {
              event: 'cart_recs',
              eventModel: {
                cart_recs_cartid: document.cookie.split('cart=')[1].split(';')[0],
              },
            };
            window.dataLayer.push(model);
          } catch (e) {
            console.log(e);
          }
        }

        document.querySelector('[data-cart-drawer-overlay]')?.classList.add('is-active');
        document.documentElement.classList.add('cart-drawer-open');

        // Hide main cart sticky footer if we are on the cart page
        document.querySelector('.cart-page__sticky-footer')?.classList.add('is-hidden-by-drawer');
      }
    }

    close() {
      document.querySelectorAll('.cart-drawer').forEach(d => d.classList.remove('is-open'));
      document
        .querySelectorAll('[data-cart-drawer-overlay]')
        .forEach(o => o.classList.remove('is-active'));
      document.documentElement.classList.remove('cart-drawer-open');

      // Show main cart sticky footer if we are on the cart page
      document.querySelector('.cart-page__sticky-footer')?.classList.remove('is-hidden-by-drawer');
    }

    async updateQuantity(element, delta) {
      const key = element.dataset.key;
      const container = element.closest('[data-qty-selector]');
      if (!container) {
        console.warn('CartDrawer: No quantity selector container found');
        return;
      }

      const input = container.querySelector('[data-qty-input]');
      let currentQuantity = parseInt(input?.value || input?.innerText || input?.textContent || 0);

      let quantity = currentQuantity + delta;

      const inventoryQty = parseInt(input?.dataset?.inventoryQuantity);
      const inventoryMgmt = input?.dataset?.inventoryManagement;
      const inventoryPolicy = input?.dataset?.inventoryPolicy;

      console.log('CartDrawer: updateQuantity', {
        key,
        delta,
        currentQuantity,
        newQuantity: quantity,
        inventoryQty,
        inventoryMgmt,
        inventoryPolicy,
      });

      if (
        inventoryMgmt === 'shopify' &&
        (inventoryPolicy === 'deny' || inventoryPolicy === 'continue') &&
        quantity > inventoryQty
      ) {
        const message = theme.strings.cart.general.quantity_error_updated
          ? `${theme.strings.cart.general.quantity_error_updated} ${quantity} \u2192 ${inventoryQty}`
          : `Quantity selected exceeds current stock. Your cart has been updated. ${quantity} \u2192 ${inventoryQty}`;

        console.log('CartDrawer: Inventory limit hit', { quantity, inventoryQty, message });

        // Ensure input is visible and reportable
        if (input) {
          // Focus the input first. For readonly inputs, this helps browsers align the bubble.
          input.focus();
          input.setCustomValidity(message);

          // Explicitly call reportValidity.
          // In some Chrome versions, it returns true but the bubble is invisible if the element is not "interactable" enough.
          const reported = input.reportValidity();
          console.log('CartDrawer: reportValidity result', reported);

          // Always show the toast as well for redundancy, since the user is reporting "nothing happens"
          // even when reportValidity returns true.
          console.log(message);
        } else {
          console.log(message);
        }

        if (inventoryPolicy === 'deny') {
          console.log('CartDrawer: Update blocked by inventory policy: deny');
          return;
        }
      }

      console.log('CartDrawer: Proceeding to sendCartUpdate', { key, quantity });
      await this.sendCartUpdate(key, Math.max(0, quantity), element);
    }

    async removeItem(element) {
      await this.sendCartUpdate(element.dataset.key, 0, element);
    }

    async sendCartUpdate(key, quantity, triggerElement) {
      console.log('CartDrawer: sendCartUpdate', { key, quantity });
      this.setLoading(true);
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity }),
        });

        const data = await res.json();
        console.log('CartDrawer: Update response', { status: res.status, ok: res.ok, data });

        if (!res.ok) {
          const message = data.description || data.message || 'Stock limit reached';
          const input = triggerElement
            .closest('[data-qty-selector]')
            ?.querySelector('[data-qty-input]');
          if (input && input.setCustomValidity) {
            input.setCustomValidity(message);
            input.reportValidity();
          } else if (triggerElement && triggerElement.setCustomValidity) {
            triggerElement.setCustomValidity(message);
            triggerElement.reportValidity();
          }

          console.log(message);
          //this.showWarning(message);

          // Refresh anyway to reset quantity in UI to what's actually in cart
          await this.refreshDrawer();
          return;
        }

        await this.refreshDrawer();
      } catch (e) {
        console.error('Cart update error:', e);
        this.setLoading(false);
      }
    }

    async refreshDrawer(openAfterRefresh = false) {
      // Robust detection of cart page
      const isCartPage = window.location.pathname.includes('/cart');
      const containerId = isCartPage ? 'cart-page' : 'cart-drawer';
      const sectionToFetch = isCartPage ? 'main-cart' : 'cart-drawer';

      try {
        const res = await fetch(`/?sections=${sectionToFetch}`);
        const data = await res.json();
        const rawHTML = data[sectionToFetch];
        if (!rawHTML) throw new Error('No section HTML returned');

        const parsed = new DOMParser().parseFromString(rawHTML, 'text/html');
        const newElement = parsed.getElementById(containerId);
        const newContent = newElement ? newElement.innerHTML : null;

        if (newContent) {
          const target = document.getElementById(containerId);
          if (target) {
            const oldSlider = document.querySelector('[data-cart-announcements]');
            const savedState = {
              announcementIndex: window._cartAnnouncementIndex || 0,
              announcementHeight: oldSlider?.style.height,
            };

            target.innerHTML = newContent;
            this.initUIComponents(savedState);
            this.updateCartCounts();

            // if (isCartPage) {
            //     window.scrollTo({ top: 0, behavior: 'smooth' });
            // }

            if (openAfterRefresh && !isCartPage) this.open();
          }
        }
      } catch (e) {
        console.error('Drawer refresh error:', e);
      } finally {
        this.setLoading(false); // ALWAYS turn off loading
      }
    }

    updateCartCounts() {
      fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          document.querySelectorAll('[data-cart-count]').forEach(el => {
            const textEl = el.querySelector('[data-cart-count-text]');
            const numEl = el.querySelector('[data-cart-count-number]');
            if (textEl)
              textEl.innerText = `${cart.items.length} ${cart.items.length === 1 ? 'item' : 'items'}`;
            if (numEl) numEl.innerText = cart.items.length;
            if (!textEl && !numEl) el.innerText = cart.items.length;
            el.classList.toggle('hidden', cart.items.length === 0);
          });
        });
    }

    setLoading(loading) {
      // Only apply loading state to child elements, never to the WHOLE drawer container
      // This ensures inputs and buttons are ALWAYS clickable
      document
        .querySelectorAll('[data-cart-drawer]')
        .forEach(d => d.classList.toggle('is-loading', loading));
      const globalLoader = document.querySelector('.cart-template__loading');
      if (globalLoader) globalLoader.classList.toggle('is-active', loading);
    }
  }

  const _initCart = () => {
    // Singleton check
    if (window._cartInitialized) return;
    window._cartInitialized = true;

    const instance = new CartDrawer();
    window.cartAPI = instance;
    window.neptuneCartDrawer = instance;
  };

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    _initCart();
  } else {
    window.addEventListener('load', _initCart);
  }
})();
